import React, { useState } from 'react';
import { Link } from 'lucide-react';
import { Brain, Shield, Database, RefreshCw, CheckCircle, Settings } from 'lucide-react';

type Integration = {
  id: string;
  name: string;
  description: string;
  status: 'active' | 'pending' | 'error';
  lastSync: string;
  type: 'hr' | 'lms' | 'assessment';
  icon: string;
};

const HRIntegration = () => {
  const [activeIntegrations, setActiveIntegrations] = useState<string[]>([]);

  const integrations: Integration[] = [
    {
      id: 'int-1',
      name: 'LinkedIn Learning',
      description: 'Sincroniza cursos y certificaciones de LinkedIn Learning',
      status: 'active',
      lastSync: '2025-03-10T15:30:00',
      type: 'lms',
      icon: 'linkedin'
    },
    {
      id: 'int-2',
      name: 'Coursera for Business',
      description: 'Integración con programas de Coursera',
      status: 'active',
      lastSync: '2025-03-10T14:45:00',
      type: 'lms',
      icon: 'coursera'
    },
    {
      id: 'int-3',
      name: 'Workday',
      description: 'Sincronización con sistema HRIS',
      status: 'pending',
      lastSync: '2025-03-09T16:20:00',
      type: 'hr',
      icon: 'workday'
    }
  ];

  const handleToggleIntegration = (integrationId: string) => {
    setActiveIntegrations(prev =>
      prev.includes(integrationId)
        ? prev.filter(id => id !== integrationId)
        : [...prev, integrationId]
    );
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'active':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
            <CheckCircle className="h-3 w-3 mr-1" />
            Activo
          </span>
        );
      case 'pending':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
            <RefreshCw className="h-3 w-3 mr-1" />
            Pendiente
          </span>
        );
      case 'error':
        return (
          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
            Error
          </span>
        );
      default:
        return null;
    }
  };

  return (
    <div className="bg-white rounded-lg shadow-sm">
      <div className="p-6 border-b">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold text-gray-900">Integraciones HR & LMS</h2>
          <button className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-accent hover:bg-accent/90">
            <Link className="h-4 w-4 mr-2" />
            Nueva Integración
          </button>
        </div>
      </div>

      <div className="p-6">
        <div className="grid grid-cols-1 gap-6">
          {integrations.map((integration) => (
            <div
              key={integration.id}
              className="border rounded-lg p-6 hover:border-accent/50 hover:shadow-sm transition-all duration-300"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start">
                  <div className="flex-shrink-0">
                    <div className="h-12 w-12 rounded-lg bg-accent/10 flex items-center justify-center">
                      <Brain className="h-6 w-6 text-accent" />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h3 className="text-lg font-medium text-gray-900">{integration.name}</h3>
                    <p className="mt-1 text-gray-600">{integration.description}</p>
                    
                    <div className="mt-2 flex items-center space-x-4">
                      {getStatusBadge(integration.status)}
                      <span className="text-sm text-gray-500">
                        Última sincronización: {new Date(integration.lastSync).toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex items-center space-x-4">
                  <button className="text-gray-400 hover:text-gray-500">
                    <Settings className="h-5 w-5" />
                  </button>
                  <div className="relative inline-block w-12 h-6">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={activeIntegrations.includes(integration.id)}
                      onChange={() => handleToggleIntegration(integration.id)}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-accent/20 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-accent"></div>
                  </div>
                </div>
              </div>

              {integration.status === 'active' && (
                <div className="mt-6 bg-gray-50 rounded-lg p-4">
                  <div className="grid grid-cols-3 gap-4">
                    <div>
                      <p className="text-sm font-medium text-gray-500">Usuarios Sincronizados</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">1,234</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Cursos Integrados</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">56</p>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-500">Certificaciones</p>
                      <p className="mt-1 text-lg font-semibold text-gray-900">89</p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        <div className="mt-8 bg-accent/5 p-6 rounded-lg">
          <div className="flex items-start">
            <Shield className="h-6 w-6 text-accent flex-shrink-0" />
            <div className="ml-4">
              <h4 className="text-lg font-medium text-gray-900">Seguridad y Cumplimiento</h4>
              <p className="mt-1 text-gray-600">
                Todas las integraciones cumplen con GDPR y estándares de seguridad empresarial
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Encriptación de datos end-to-end
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Autenticación de dos factores
                </li>
                <li className="flex items-center text-sm text-gray-600">
                  <CheckCircle className="h-4 w-4 text-green-500 mr-2" />
                  Auditoría de accesos
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default HRIntegration;